
import { Component, OnDestroy, effect, input } from '@angular/core';
import { BotCardType, BotMessageFeedback, ChatbotConfig, HistoryItemResponse, isIdleState, SessionState } from '@shared/index';
import { Subscription } from 'rxjs';
import { BotMessagesService } from 'src/app/shared/services/bot-messages.service';
import { BotService } from 'src/app/shared/services/bot.service';
import { EventManagerAdminService } from 'src/app/shared/services/event-manager/event-manager-admin.service';
import { KeyValAdminService } from 'src/app/shared/services/key-val-admin.service';
import { LoginService } from '@shared-angular/services/login.service';
import { SocketService } from 'src/app/shared/services/socket-admin.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ThinkingAnimationComponent } from "@shared-angular/components/thinking-animation/thinking-animation.component";
import { AiAnnotation, MessageBubbleComponent, Remark } from './message-bubble/message-bubble.component';
import { MessageCardComponent } from './message-card/message-card.component';

@Component({
  selector: 'app-bot-chat-dialog',
  templateUrl: './bot-chat-dialog.component.html',
  styleUrls: ['./bot-chat-dialog.component.scss'],
  imports: [MessageBubbleComponent, MessageCardComponent, ThinkingAnimationComponent]
})
export class BotChatDialogComponent implements OnDestroy {
  public readonly annotations = input<AiAnnotation[]>([]);
  public readonly isThinking = input<boolean>(false);
  public readonly isSending = input<boolean>(false);
  public readonly botConfig = input.required<ChatbotConfig>();
  public readonly botState = input<SessionState>("idle");
  public readonly enableDeletionOverride = input<boolean>(false);
  public readonly enableFeedbackOverride = input<boolean>(false);
  public readonly showHistory = input<boolean>(false);
  public readonly debugMode = input<boolean>(false);

  public wasIdle = false;
  public msgIdToFeedbackMap: Map<string, BotMessageFeedback> = new Map<string, BotMessageFeedback>();
  private subscriptions: Subscription[] = [];

  constructor(
    private botService: BotService,
    public botMessagesService: BotMessagesService,
    private toastr: ToastrService,
    private socketService: SocketService,
    protected keyvalService: KeyValAdminService,
    private eventManager: EventManagerAdminService,
    private loginService: LoginService
  ) {
    this.subscriptions.push(
      this.socketService.onFeedbackUpdate.subscribe(data => {
        const message = data.feedback.messages[data.feedback.messages.length - 1];
        if (message) {
          this.msgIdToFeedbackMap.set(message._id, data.feedback);
          this.toastr.info("ADD_FEEDBACK");
        }
      }),

      this.socketService.onFeedbackDeletion.subscribe(data => {
        const message = data.feedback.messages[data.feedback.messages.length - 1];
        if (message) {
          this.msgIdToFeedbackMap.delete(message._id);
          this.toastr.info("REM_FEEDBACK");
        }
      })
    );
    this.eventManager.on('RAGAI.CARDS.loading', (_event, cardData) => {
      const data = cardData as { type: BotCardType.loadingCard, data: { text: string, cssClass: string, icon: string } };
      console.log(data);
      this.botMessagesService.addMessage({
        _id: 'loadingCard',
        context: {
          botid: this.botService.botConfig?._id ?? '',
          user: this.loginService.loggedInUser?._id ?? ''
        },
        created: new Date(),
        memoryEnabled: this.botService.botConfig?.memoryEnabled ?? true,
        threadId: '',
        item_id: 'loadingCard',
        supervisorEnabled: this.botService.botConfig?.supervisorEnabled ?? true,
        role: 'botcard',
        content: [{
          text: JSON.stringify(data),
          type: 'text'
        }]
      });
      void this.botService.scrollDown();
    });
    this.eventManager.on('RAGAI.CARDS.removeLoading', (_event, _cardData) => {
      this.botMessagesService.deleteMessage('loadingCard');
    });
    effect(() => {
      this.botConfig();
      if (this.showHistory()) void this.loadHistory();
    });
    effect(() => {
      if (isIdleState(this.botState()) && !this.wasIdle && this.showHistory()) {
        this.wasIdle = true;
        void this.loadHistory();
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => { s.unsubscribe(); });
  }

  public getLoadingMessage(): string {
    if (this.isSending()) { return $localize`:@@bot-chat-dialog-loadingMessage-send:Sende Daten...`; }
    const botState = this.botState();
    if (botState === 'init_assistant') { return $localize`:@@bot-chat-dialog-loadingMessage-assistant:Assistant wird erstellt`; }
    if (botState === 'init_thread') { return $localize`:@@bot-chat-dialog-loadingMessage-thread:Thread wird erstellt`; }
    if (botState === 'init_files') { return $localize`:@@bot-chat-dialog-loadingMessage-vector:Dateien werden vektorisiert`; }
    if (botState === 'requesting_tools') { return $localize`:@@bot-chat-dialog-loadingMessage-tools:Tools werden angefragt`; }
    if (botState === 'requesting_websearch') { return $localize`:@@bot-chat-dialog-loadingMessage-websearch:Suche wird ausgeführt`; }
    return $localize`:@@bot-chat-dialog-loadingMessage-receiving:Empfange...`;
  }

  public async loadFeedback() {
    const feedbacks = (await this.botService.getMyFeedback(this.botConfig()._id)).feedbacks;
    this.msgIdToFeedbackMap.clear();
    feedbacks.forEach(feedback => {
      const message = feedback.messages[feedback.messages.length - 1];
      if (message) {
        this.msgIdToFeedbackMap.set(message._id, feedback);
      }
    });
  }

  public async loadHistory(): Promise<void> {
    if (!this.showHistory()) return;
    if (this.botMessagesService.getMessageCount() > 0) return;
    this.botMessagesService.setMessages((await this.botService.getHistoryMessages(this.botConfig()._id)).messageHistory);
    await this.loadFeedback();
    void this.botService.scrollDown('instant');
  }

  public async deleteMessage(message: HistoryItemResponse): Promise<void> {
    await this.botService.deleteHistoryMessages(this.botConfig()._id, [message._id]);
    this.botMessagesService.deleteMessage(message._id);
  }

  public async sendFeedback(rawFeedback: { remark: Remark, msgId: string }) {
    const index = this.botMessagesService.findMessageIndex(rawFeedback.msgId);
    if (index < 0) return;
    const message = this.botMessagesService.getMessage(index);
    if (!message) return;
    if (rawFeedback.remark === '') {
      const feedback = this.msgIdToFeedbackMap.get(message._id);
      if (!feedback) return;
      await this.botService.deleteFeedback(feedback._id);
    } else {
      const messages = [message];
      if (index > 0) {
        const previousMessage = this.botMessagesService.getMessage(index - 1);
        if (previousMessage?.role === 'user' && !previousMessage.bypass) {
          messages.unshift(previousMessage);
        }
      }
      const feedbackId = this.msgIdToFeedbackMap.get(message._id)?._id;
      await this.botService.sendFeedback(feedbackId, { botId: this.botConfig()._id, remark: rawFeedback.remark, messages });
    }
  }
}